import { useEffect } from 'react';
import { paths, pick, prop } from 'ramda';

import { usePortletConfig } from 'js/components/system/Portlet';

import {
    useGetFeedBlacklist
} from '@interlayer/js/components/system/App/hooks/useGetGamesConfiguration/helpers';

import {
    useCurrentPathAsGameCategory
} from 'js/app/components/main/Games/components/ControlPanel/hooks/useCurrentPathAsGameCategory';
import { DYNAMIC_CATEGORIES } from 'js/app/components/main/Games/constants';
import { useGameComponentConfig } from 'js/app/components/main/Games/hooks/useGameComponentConfig';
import { useGetFeed } from 'js/app/components/main/Games/hooks/useGetFeed';
import { setFeedAction } from 'js/app/components/main/Games/store/actions';
import { useGamesDispatch } from 'js/app/components/main/Games/store/context';
import { getPositionExcludingExisting } from 'js/app/components/main/Games/utils';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

export const useSetGamesConfig = (feed) => {
    const gameFeed = useGetFeed();
    const { gamesFeeds, isGamesLoaded } = useAppSelector(pick(['gamesFeeds', 'isGamesLoaded']));
    const dispatch = useGamesDispatch();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const { gameNameCategory } = useCurrentPathAsGameCategory();
    const getGamesBlacklist = useGetFeedBlacklist();
    const [customCategories, defaultGameFeed = feed] = paths([
        ['customCategories', 'category'],
        ['gamesConfiguration', 'gameFeed']
    ], usePortletConfig());

    const {
        viewMode,
        allPosition = 0,
        recentlyPosition = 0,
        favouritesPosition = 0,
        showAllCategory: enableAllCategory,
        showRecently: enableRecentlyCategory,
        showFavouritesCategory: enableFavouritesCategory
    } = useGameComponentConfig();

    useEffect(() => {
        const gamesBlacklist = getGamesBlacklist(gamesFeeds);

        if (!isGamesLoaded) {
            return;
        }

        const dynamicCategories = [
            {
                name: DYNAMIC_CATEGORIES.ALL_CATEGORY,
                enabled: enableAllCategory && viewMode !== 'inline',
                position: allPosition
            },
            {
                name: DYNAMIC_CATEGORIES.RECENTLY,
                enabled: enableRecentlyCategory && isLoggedIn,
                position: recentlyPosition
            },
            {
                name: DYNAMIC_CATEGORIES.FAVOURITES,
                enabled: enableFavouritesCategory && isLoggedIn,
                position: favouritesPosition
            }
        ]
            .filter(dynamicCategory => dynamicCategory.enabled)
            .map((category) => ({
                isBingoList: customCategories?.find(custom => custom.categoryName === category.name)?.isBingoScheduler || false,
                ...category
            }));

        if (
            !gameFeed
            && (
                !dynamicCategories.length
                || dynamicCategories.length === 1 && dynamicCategories[0].name === DYNAMIC_CATEGORIES.ALL_CATEGORY
            )
        ) {
            dispatch(setFeedAction({
                feed: {
                    name: '',
                    categories: []
                },
                isEmptyFeed: true,
                dynamicCategories: [],
                categories: [],
                activeCategory: {}
            }));

            return;
        }

        const currentFeed = gameFeed || {
            name: defaultGameFeed,
            categories: []
        };

        const getPositionCategory = getPositionExcludingExisting({
            initialPosition: 1,
            existingPositions: dynamicCategories.map(({ position }) => position)
        });

        const categoriesWithPosition = currentFeed.categories
            .map((category, index) => {
                const {
                    gameInjected,
                    isBingoScheduler
                } = customCategories?.find(custom => custom.categoryName === category.name) || {};

                let { gameCodes } = category;

                // GAME INJECTIONS
                if (gameInjected?.length) {
                    const injectedGameCodeList = gameInjected.map(({ gameCode }) => gameCode);
                    const infusedGameCodes = category.gameCodes
                        .filter((gameCode) => !injectedGameCodeList.includes(gameCode) && !gamesBlacklist.includes(gameCode));

                    gameInjected
                        .filter(game => !gamesBlacklist.includes(game.gameCode))
                        .forEach(injectedGame => {
                            infusedGameCodes.splice(injectedGame.position - 1, 0, injectedGame.gameCode);
                        });

                    gameCodes = infusedGameCodes;
                }

                return {
                    ...category,
                    position: getPositionCategory(index),
                    isBingoList: isBingoScheduler || false,
                    gameCodes
                };
            });

        const fullCategories = [...dynamicCategories, ...categoriesWithPosition]
            .sort((categoryA, categoryB) => categoryA.position - categoryB.position);

        dispatch(setFeedAction({
            feed: {
                ...currentFeed,
                categories: categoriesWithPosition
            },
            isEmptyFeed: false,
            dynamicCategories,
            categories: fullCategories,
            activeCategory: gameNameCategory
                ? fullCategories.find(cat => cat.name === gameNameCategory) || fullCategories[0] || {}
                : fullCategories[0] || {}
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameFeed, isLoggedIn, isGamesLoaded]);
};
